import { CreditTransaction } from '../../lib/api';

interface TransactionHistoryProps {
  transactions: CreditTransaction[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

function getTypeLabel(type: CreditTransaction['type']): string {
  switch (type) {
    case 'grant':
      return 'Credit Grant';
    case 'usage':
      return 'Usage';
    case 'refund':
      return 'Refund';
    case 'adjustment':
      return 'Adjustment';
    default:
      return type;
  }
}

function getTypeStyles(type: CreditTransaction['type']): string {
  switch (type) {
    case 'grant':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'usage':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'refund':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'adjustment':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

export function TransactionHistory({
  transactions,
  total,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
}: TransactionHistoryProps) {
  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-48 mb-4"></div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="py-4 border-b border-border last:border-0">
              <div className="flex justify-between items-center">
                <div className="h-4 bg-muted rounded w-32"></div>
                <div className="h-4 bg-muted rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border">
      <div className="p-6 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">
          Transaction History
          <span className="text-sm font-normal text-muted-foreground ml-2">({total} total)</span>
        </h2>
      </div>

      {transactions.length === 0 ? (
        <div className="p-6 text-center text-muted-foreground">No transactions yet</div>
      ) : (
        <div className="divide-y divide-border">
          {transactions.map((txn) => (
            <div key={txn.transactionId} className="p-4 hover:bg-muted/50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded ${getTypeStyles(txn.type)}`}
                    >
                      {getTypeLabel(txn.type)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(txn.createdAt).toLocaleString()}
                    </span>
                  </div>
                  {txn.description && (
                    <p className="text-sm text-muted-foreground">{txn.description}</p>
                  )}
                  {txn.requestId && (
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      Request: {txn.requestId}
                    </p>
                  )}
                </div>
                <div className="text-right ml-4">
                  <div
                    className={`font-semibold ${
                      txn.amount >= 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {txn.amount >= 0 ? '+' : ''}${(txn.amount / 100).toFixed(2)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Balance: ${(txn.balanceAfter / 100).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="p-4 border-t border-border text-center">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 disabled:text-muted-foreground"
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
}
